import { Controller } from "@hotwired/stimulus"
import { ensureChart, ensureLeaflet } from "./vendor_loader"
import { leafletTileConfig } from "../tile_sources"
import { elevationChartSeries, elevationSegments, sampleTripPoints, tripPointReadout } from "../lib/trip_profile"
import { logEvent } from "../lib/client_logger"

let L

export default class extends Controller {
  static targets = ["map", "chart"]
  static values = { points: Array }

  connect() {
    this.connected = true
    this.#boot().catch(error => this.#bootFailed(error))
  }

  disconnect() {
    this.connected = false
    if (this.chart) this.chart.destroy()
    if (this.map) this.map.remove()
  }

  async #boot() {
    const chartPromise = this.hasChartTarget ? ensureChart() : Promise.resolve(null)
    await Promise.all([ensureLeaflet(), chartPromise])
    if (!this.connected) return

    L = window.L
    // Guard against an oversized embed (the server already samples): drawing
    // tens of thousands of segment paths freezes the tab. Map + chart + hover
    // all work off the same sampled set.
    this.points = sampleTripPoints(this.pointsValue || [])
    this.latlon = this.points.map(point => [point.lat, point.lon])
    if (this.latlon.length < 2) return

    this.#renderMap()
    this.#renderChart()
  }

  #bootFailed(error) {
    if (!this.connected) return
    logEvent("trip_map.initialize_failed", error)
  }

  #renderMap() {
    // Canvas, not SVG: the elevation-colored route is one path per segment, and
    // thousands of SVG nodes lock the renderer where one canvas paint is cheap.
    this.map = L.map(this.mapTarget, { scrollWheelZoom: false, preferCanvas: true })
    const satellite = leafletTileConfig("satellite")
    L.tileLayer(satellite.url, satellite.options).addTo(this.map)

    L.polyline(this.latlon, { color: "#fff", weight: 6, opacity: 0.9 }).addTo(this.map)
    const segments = elevationSegments(this.points)
    if (segments.length) {
      segments.forEach(segment => {
        L.polyline([segment.from, segment.to], { color: segment.color, weight: 3 }).addTo(this.map)
      })
    } else {
      L.polyline(this.latlon, { color: "#2563eb", weight: 3 }).addTo(this.map)
    }

    this.map.fitBounds(L.latLngBounds(this.latlon), { padding: [25, 25] })
    L.marker(this.latlon[0], { icon: this.#dot("#16a34a") }).addTo(this.map).bindPopup("Start")
    L.marker(this.latlon[this.latlon.length - 1], { icon: this.#dot("#dc2626") }).addTo(this.map).bindPopup("End")

    this.highlight = L.circleMarker(this.latlon[0], {
      radius: 7, color: "#fff", weight: 2, fillColor: "#111827", fillOpacity: 1
    })
    this.highlight.bindTooltip("", { direction: "top", offset: [0, -6], opacity: 0.95 })

    const hit = L.polyline(this.latlon, { opacity: 0, weight: 18 }).addTo(this.map)
    hit.on("mousemove", event => this.#showAt(event.latlng))
    hit.on("click", event => this.#showAt(event.latlng))
    hit.on("mouseout", () => this.#hideHighlight())
  }

  #renderChart() {
    if (!this.hasChartTarget || !window.Chart) return
    const series = elevationChartSeries(this.points)
    if (!series.data.length) return

    this.chart = new window.Chart(this.chartTarget, {
      type: "line",
      data: {
        labels: series.labels,
        datasets: [{
          data: series.data,
          borderColor: "#1b5e20",
          backgroundColor: "rgba(27,94,32,0.12)",
          fill: true,
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        onHover: (_event, elements) => {
          if (elements.length) this.highlight.setLatLng(series.coords[elements[0].index]).addTo(this.map)
        },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { title: context => `${context[0].label} mi`, label: context => `${context.parsed.y} ft` } }
        },
        scales: {
          x: { title: { display: true, text: "miles" }, grid: { display: false }, ticks: { maxTicksLimit: 6, color: "#94a3b8" } },
          y: { title: { display: true, text: "ft" }, ticks: { color: "#94a3b8" }, grid: { color: "rgba(148,163,184,0.15)" } }
        }
      }
    })
    this.chartTarget.addEventListener("mouseleave", () => this.#hideHighlight())
  }

  #nearest(latlng) {
    let best = 0
    let distance = Infinity
    for (let i = 0; i < this.latlon.length; i += 1) {
      const candidate = this.map.distance(latlng, this.latlon[i])
      if (candidate < distance) {
        distance = candidate
        best = i
      }
    }
    return best
  }

  #showAt(latlng) {
    const index = this.#nearest(latlng)
    this.highlight.setLatLng(this.latlon[index]).addTo(this.map)
    this.highlight.setTooltipContent(this.#tooltipHtml(index))
    this.highlight.openTooltip()
  }

  #hideHighlight() {
    if (!this.highlight || !this.map) return
    this.highlight.closeTooltip()
    this.map.removeLayer(this.highlight)
  }

  #tooltipHtml(index) {
    const info = tripPointReadout(this.points, index)
    return `<div style="line-height:1.35;text-align:center"><b>${info.elevation}</b><br>${info.speed} · ${info.heading}</div>`
  }

  #dot(color) {
    return L.divIcon({
      className: "",
      html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,.5)"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7]
    })
  }
}
